// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import type {
  CsvImportFailedRow,
  CsvImportResultBucket,
  CsvImportResultsView,
} from '@/csv-import/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { formatFailedRowsForCopy } from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  VariableSizeList as VariableVirtualList,
  FixedSizeList as VirtualList,
} from 'react-window';

const ITEM_HEIGHT = 24;
const FAILED_ROW_BASE_HEIGHT = 56;
const FAILED_ROW_ERROR_HEIGHT = 24;
const COPY_FEEDBACK_RESET_MS = 2000;

function failedRowHeight(row: CsvImportFailedRow): number {
  return FAILED_ROW_BASE_HEIGHT + row.errors.length * FAILED_ROW_ERROR_HEIGHT;
}

interface CsvImportResultsStepProps {
  view: CsvImportResultsView | null;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface BucketRowProps {
  index: number;
  style: React.CSSProperties;
  data: CsvImportResultBucket['rows'];
}

function BucketRow(props: BucketRowProps) {
  const row = props.data[props.index];

  // Caller content of an unknown height scrolls inside its slot rather than
  // painting over the next row
  return (
    <div style={{ ...props.style, overflowY: 'auto' }}>
      <ListItem component="div" dense sx={{ py: 0 }}>
        <ListItemText
          primary={row.content}
          primaryTypographyProps={{ component: 'div' }}
          sx={{ my: 0 }}
        />
      </ListItem>
    </div>
  );
}

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: CsvImportFailedRow[];
}

function FailedRowItem(props: FailedRowItemProps) {
  const row = props.data[props.index];

  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={row.primaryText}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    </div>
  );
}

export function CsvImportResultsStep(props: CsvImportResultsStepProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (!props.view) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, buckets, failedRows, failedRowNoun, structureErrors } =
    props.view;
  const hasStructureErrors = structureErrors.length > 0;
  const hasRowErrors = failedRows.length > 0;
  const failedRowsHeight = failedRows.reduce(
    (total, row) => total + failedRowHeight(row),
    0
  );
  const uploadSucceeded = success && !hasStructureErrors && !hasRowErrors;

  const handleToggleExpanded = (section: string) => {
    setExpanded((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  };

  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = formatFailedRowsForCopy(
        failedRows,
        (row) => row.identifierSuffix
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_RESET_MS);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  return (
    <Card>
      <CardContent>
        {uploadSucceeded ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <AlertTitle>Success</AlertTitle>
            <Typography variant="body2">{props.view.successMessage}</Typography>
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 3 }}>
            {hasStructureErrors && (
              <>
                <AlertTitle>File format invalid</AlertTitle>
                <Typography variant="body2">
                  The uploaded file has columns that cannot be used with this
                  upload
                </Typography>
              </>
            )}
            {hasRowErrors && (
              <>
                <AlertTitle>Uploaded data invalid</AlertTitle>
                <Typography variant="body2">
                  The uploaded file includes rows that do not match required
                  fields
                </Typography>
              </>
            )}
          </Alert>
        )}

        {buckets.map(
          (bucket) =>
            bucket.rows.length > 0 && (
              <Paper
                key={bucket.key}
                variant="outlined"
                sx={{ mb: 2, overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor:
                      bucket.tone === 'success' ? 'success.light' : 'grey.300',
                    color:
                      bucket.tone === 'success'
                        ? 'success.contrastText'
                        : 'text.primary',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleToggleExpanded(bucket.key)}
                >
                  <Box display="flex" alignItems="center">
                    {bucket.tone === 'success' && (
                      <CheckCircleIcon sx={{ mr: 1 }} />
                    )}
                    <Typography variant="subtitle1">
                      {`${bucket.title} (${formatNumber(bucket.rows.length)})`}
                    </Typography>
                  </Box>
                  {expanded[bucket.key] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={expanded[bucket.key]} sx={{ pt: 1 }}>
                  <VirtualList
                    height={Math.min(bucket.rows.length * ITEM_HEIGHT, 200)}
                    itemCount={bucket.rows.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={bucket.rows}
                    width="100%"
                  >
                    {BucketRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )
        )}

        {hasStructureErrors && (
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'error.light',
                color: 'error.contrastText',
                cursor: 'pointer',
              }}
              onClick={() => handleToggleExpanded('structureErrors')}
            >
              <Box display="flex" alignItems="center">
                <ErrorIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  {`Column errors (${formatNumber(structureErrors.length)})`}
                </Typography>
              </Box>
              {expanded['structureErrors'] ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </Box>
            <Collapse in={expanded['structureErrors']}>
              <Box
                component="ul"
                sx={{
                  maxHeight: '300px',
                  overflow: 'auto',
                  listStyleType: 'disc',
                }}
              >
                {structureErrors.map((row, index) => (
                  <li
                    key={`structure-${row.fieldName}-${index}`}
                    style={{ paddingBottom: '8px' }}
                  >
                    {row.fieldName
                      ? `${row.fieldName}: ${row.error}`
                      : row.error}
                  </li>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {hasRowErrors && (
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'error.light',
                color: 'error.contrastText',
                cursor: 'pointer',
              }}
              onClick={() => handleToggleExpanded('failed')}
            >
              <Box display="flex" alignItems="center">
                <ErrorIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  {`Invalid rows (${formatNumber(failedRows.length)} ${pluralize(
                    failedRows.length,
                    {
                      includeCount: false,
                      singular: failedRowNoun.singular,
                      plural: failedRowNoun.plural,
                    }
                  )})`}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={isCopied ? 'Copied!' : 'Copy all errors'} arrow>
                  <IconButton
                    size="small"
                    onClick={handleCopyErrors}
                    aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                    sx={{
                      color: 'error.contrastText',
                      '&:hover': {
                        bgcolor: 'error.dark',
                      },
                    }}
                  >
                    {isCopied ? (
                      <CheckRounded fontSize="small" />
                    ) : (
                      <ContentCopyRounded fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                {expanded['failed'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            </Box>
            <Collapse in={expanded['failed']}>
              <VariableVirtualList
                height={Math.min(failedRowsHeight, 300)}
                itemCount={failedRows.length}
                itemSize={(index) => failedRowHeight(failedRows[index])}
                itemData={failedRows}
                width="100%"
              >
                {FailedRowItem}
              </VariableVirtualList>
            </Collapse>
          </Paper>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
